
import { Col, List, Pagination, Popconfirm, Row, Table, Tag, Tooltip, notification } from 'antd';
import BookingInfo, { BookingCurrencyTotal, SmallBookingDataList } from '../../../models/BookingInfo';
import './BookingsListComponent.scss'
import { useTranslation } from 'react-i18next';
import { EditOutlined, InfoCircleOutlined, LoadingOutlined, ReloadOutlined, SnippetsOutlined, SyncOutlined, UserOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import moment from 'moment';
import { useAppConfig } from '../../../Contexts/AppConfigContext';
import { GetPaginationLocale } from '../../../utils/locales';
import { useEffect, useState } from 'react';
import HTMLReactParser from 'html-react-parser';
import MoneyComponent from '../../common/MoneyComponent/MoneyComponent';
import { BookingListItem } from '../..';
import { useSessionData } from '../../../Contexts/SessionDataContext';
import { useCurrencyData } from '../../../Contexts/CurrencyContext';
import { cancelBookingRewards, ImportBookingRewards } from '../../../api/repositoryEP';
import { ImportBookingRewardsData } from '../../../models/booking/ImportBookingRewards';
import { BookingStatusInfo, GUEST } from '../../../utils/constants';

interface BookingsListComponentProps {
    bookingsInfo: SmallBookingDataList,
    page: number,
    pageSize: number,
    search: (rows: number, page: number) => void,
    onShowBookingDetail: (bookingId: string) => void,
    onShowCustomerVoucher: (bookingId: string) => void,
    synchronizingId: string,
    totalsByCurrency: BookingCurrencyTotal[],
    totalElements: number,
    loading: boolean
}

const BookingsListComponent = (props: BookingsListComponentProps): JSX.Element => {
    const { t } = useTranslation();
    const sessionData = useSessionData();
    // sessionData.userData.UAID   Si el cliente tiene contratado rewards, pero no tiene el UAID, no se le muestra la opción de rewards



    const appConfig = useAppConfig(); // aqui tenemos si el cliente tiene rewars o no
    // Las recompensas solo se muestran si el cliente tiene rewards contratado y el usuario tiene UAID
    const showRewards = !!appConfig.config.hasRewardsContracted && !!sessionData.userData.uaid;

    // Los totales llegan por divisa: cada uno se pasa a la divisa elegida por el usuario y se suman, como el importe de cada reserva
    const { currencyData, doExchangeCurrency } = useCurrencyData();
    const getTotal = (field: 'pvp' | 'commission'): JSX.Element => {
        const totals = props.totalsByCurrency.map(total => ({ currency: total.currency ?? sessionData.userData.currency ?? 'EUR', amount: total[field] }));
        const amount = totals.reduce((sum, total) => sum + doExchangeCurrency(total.currency, currencyData.currencySelectedISO, total.amount), 0);
        const detail = totals.some(total => total.currency !== currencyData.currencySelectedISO) ? totals.map(total => `${total.amount} ${total.currency}`).join(' + ') : '';
        return <Tooltip title={detail}>{Math.round(amount * 100) / 100} {currencyData.currencySelectedISO}</Tooltip>;
    }

    const getBookingDetail = (bookingId: string, locator: string): void => {
        props.onShowBookingDetail(bookingId);
    }

    const getCustomerVoucher = (locatorId: string): void => {
        props.onShowCustomerVoucher(locatorId);
    }


    const getTagStatus = (record: BookingInfo): JSX.Element => {
        const colors: { [key: string]: string } = {
            'pending': 'blue',
            'pre': 'orange',
            'active': 'green',
            'canceled': 'red',
            'error': 'red',
            'pending_payment': 'purple',
            'postponed': 'orange'
        }

        //Cogemos el color de colors según el status del booking
        const tagColor = colors[record.statusName];

        return <div className="status">
            <Tag className="tag-status" color={tagColor}>
                {t(`booking-status.${record?.statusName}`)}
                {
                    record?.isCancelled ?
                        <Tooltip title={t('pages.booking-detail.booking-status.cancelled-at', [record.cancelledDate ? moment(record.cancelledDate).format(appConfig.config.dateFormat) : 'N/D (1)'])}>
                            <InfoCircleOutlined className="icon-cancelled-at" />
                        </Tooltip>
                        :
                        <></>
                    //<SynchronizeButton synchronizing={props.synchronizingId === record.locator} bookingData={record} onSynchronize={reloadBookingInfo}/>
                }
                {
                    (record?.statusName == "error" && record?.statusInfo) ?
                        <Tooltip title={
                            record.statusInfo === BookingStatusInfo.ERROR_NOT_REFUNDED
                                ? t('pages.booking-detail.booking-status.error-not-refunded')
                                : record.statusInfo
                        }>
                            <InfoCircleOutlined className="icon" />
                        </Tooltip>
                        :
                        <></>
                }

            </Tag>
        </div>
    }

    function calculateQuantityToBePaid(total: number, paid: number): number {
        return total - paid;
    }


    const getprice = (record: BookingInfo): JSX.Element => {
        return (
            <div className="pricing-data">
                <div className="pvp">
                    <span>
                        {t('components.bookings-list.pvp') + ": "}
                        <MoneyComponent amountOriginal={record.pvp} currencyOriginal={record.currency} />
                    </span>
                </div>
                <div className="net">
                    <span>
                        {t('components.bookings-list.net') + ": "}
                        <MoneyComponent amountOriginal={record.net} currencyOriginal={record.currency} />
                    </span>
                </div>
                <div className="commission">
                    <span>
                        {t('components.bookings-list.commission') + ": "}
                        <MoneyComponent amountOriginal={record.commission} currencyOriginal={record.currency} />
                    </span>
                </div>
                <div className="payed">
                    <span>
                        {t('components.bookings-list.payed') + ": "}
                        <MoneyComponent amountOriginal={record.payed} currencyOriginal={record.currency} />
                    </span>
                </div>

                {(record.statusName == 'active' && record.paymentMethodCode != null &&
                    record.paymentCardUsed != null &&
                    record.paymentMethodCode.toLocaleLowerCase().includes("tpv")) && (
                        <>
                            <div className="card-used">
                                <span>
                                    {record.paymentCardUsed == GUEST ? t('pages.booking-detail.total-resume.payed-by-guest') : t('pages.booking-detail.total-resume.payed-by-agent')}
                                </span>
                            </div>
                            <div className="to-be-payed">
                                <span>
                                    {t('components.bookings-list.to-be-payed') + ": "}
                                    <span className={calculateQuantityToBePaid(record?.totalToPay || 0, record?.payed || 0) > 0 ? "red" : ""}>
                                        <MoneyComponent amountOriginal={calculateQuantityToBePaid(record.totalToPay || 0, record.payed || 0)} currencyOriginal={record.currency} />
                                    </span>
                                </span>
                            </div>
                        </>

                    )}

            </div>
        );
    };


    const getLocatorInfo = (record: BookingInfo): JSX.Element => {
        return <div className="locator-data">
            <div className="locator">{record.locator}</div>
            <div className="creation-date">({moment(record.creationDate).format(appConfig.config.dateFormat)})</div>
        </div>
    }

    const getDatesInfo = (record: BookingInfo): JSX.Element => {
        return <div className="hotel-data">
            <div className="hotel-name">{record.hotelName}</div>
            <div className="check-in-out">
                ({moment(record.checkIn).format(appConfig.config.dateFormat)} - {moment(record.checkOut).format(appConfig.config.dateFormat)})
            </div>
        </div>
    }

    const getUserInfo = (record: BookingInfo): JSX.Element => {
        return <div className="user-data">
            <div className="user-name">{record.userName}</div>
            <div className="user-email">{record.userEmail}</div>
        </div>
    }
    const [loadingBookingId, setLoadingBookingId] = useState<string | null>(null);
    const [bookingsData, setBookingsData] = useState<BookingInfo[]>([]);
    useEffect(() => { }, [bookingsData])

    useEffect(() => {
        if (props.bookingsInfo?.elements) {
            setBookingsData(props.bookingsInfo.elements);
        }
    }, [props.bookingsInfo]);





    const retryImportBooking = (record: BookingInfo) => {
        setLoadingBookingId(record.bookingId);

        const data: ImportBookingRewardsData = {
            bookingId: record.bookingId,
        };

        ImportBookingRewards(data).then((response) => {
            if (response?.type === 1 && response.data != null) {
                setBookingsData(prev =>
                    prev.map(item =>
                        item.bookingId === record.bookingId
                            ? {
                                ...item,
                                imported: true,
                                reward: response.data, // Asumiendo que response.data.reward existe
                            }
                            : item
                    )
                );
                props.search(props.pageSize, props.page);
            }
            setLoadingBookingId(null);
        });
    };



    const retryCancelBooking = (record: BookingInfo) => {
        setLoadingBookingId(record.bookingId);

        cancelBookingRewards(record.bookingId).then((response) => {
            if (response?.type === 1 && response.data != null) {
                setBookingsData(prev =>
                    prev.map(item =>
                        item.bookingId === record.bookingId
                            ? {
                                ...item,
                                imported: true,
                                reward: response.data, // Asumiendo que response.data.reward existe
                            }
                            : item
                    )
                );
                props.search(props.pageSize, props.page);
            }
        }).finally(() => {
            setLoadingBookingId(null);

        });
    };




    const getRewardsInfo = (record: BookingInfo): JSX.Element => {
        return (
            <div className="reward-data" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {/*mostraricono de reintentar si está en estado en cancelada y se quiere sincronizar*/}

                {record.statusName === 'canceled' &&
                    sessionData.userData.uaid &&
                    Number(record.reward) > 0 && (
                    <Tooltip title={t('components.bookings-list.actions-tooltip.retry-synchronization') || ""}>
                        {loadingBookingId === record.bookingId ? (
                            <LoadingOutlined style={{ color: 'gray', cursor: 'not-allowed' }} />
                        ) : (
                            <ReloadOutlined
                                style={{ color: 'red', cursor: 'pointer' }}
                                onClick={() => retryCancelBooking(record)}
                            />
                        )}
                    </Tooltip>
                )}
                {/* Mostrar icono info si falta UAID y el cliente tiene contratado */}
                {!sessionData.userData.uaid && record.statusName === 'active' && !record.imported && (
                    <Tooltip title={t('components.bookings-list.actions-tooltip.info-retry-synchronization') || ""}>
                        <InfoCircleOutlined style={{ color: "#faad14" }} />
                    </Tooltip>
                )}

                {/* Mostrar recompensa si imported = true */}
                {record.imported === true && record.statusName === 'active' && (
                    <div className="reward">
                        <MoneyComponent amountOriginal={record.reward} currencyOriginal={record.currency} />
                    </div>
                )}

                {/* Mostrar botón de reintentar si imported = false, estado activo y UAID presente */}
                {record.imported === false &&
                    record.statusName === 'active' &&
                    sessionData.userData.uaid && (
                        <Tooltip title={t('components.bookings-list.actions-tooltip.retry-synchronization') || ""}>
                            {loadingBookingId === record.bookingId ? (
                                <LoadingOutlined style={{ color: 'gray', cursor: 'not-allowed' }} />
                            ) : (
                                <ReloadOutlined
                                    style={{ color: 'red', cursor: 'pointer' }}
                                    onClick={() => retryImportBooking(record)}
                                />
                            )}
                        </Tooltip>
                    )}

            </div>

        );
    };

    const tableColumns: ColumnsType<BookingInfo> = [
        {
            title: t('components.bookings-list.status'),
            dataIndex: "status",
            key: "status",
            sorter: (a: BookingInfo, b: BookingInfo) => a.statusName.localeCompare(b.statusName),
            render: (item: string, record: BookingInfo) => {

                return <div className="locator-data">
                    {getTagStatus(record)}
                </div>
            }
        },
        {
            title: t('components.bookings-list.locator'),
            dataIndex: "locator",
            key: "locator",
            sorter: (a: BookingInfo, b: BookingInfo) => a.locator.localeCompare(b.locator),
            render: (item: string, record: BookingInfo) => {

                return getLocatorInfo(record)
            },

        },
        {
            title: t('components.bookings-list.booking'),
            dataIndex: "hotelName",
            key: "hotelName",
            sorter: (a: BookingInfo, b: BookingInfo) => a.hotelName.localeCompare(b.hotelName),
            render: (item: string, record: BookingInfo) => {

                return getDatesInfo(record)
            }
        },
        {
            title: t('components.bookings-list.owner'),
            dataIndex: "ownerName",
            key: "ownerName",
            sorter: (a: BookingInfo, b: BookingInfo) => a.ownerName.localeCompare(b.ownerName),
            render: (item: string, record: BookingInfo) => {
                return <div className="owner-data">
                    <div className="owner-name">{item}</div>
                </div>
            }
        },
        Table.EXPAND_COLUMN,
        {
            title: t('components.bookings-list.pricing-data'),
            dataIndex: "pvp",
            key: "pvp",
            render: (item: string, record: BookingInfo) => {

                return record.isPvp ? <MoneyComponent amountOriginal={record.pvp} currencyOriginal={record.currency} /> : <MoneyComponent amountOriginal={record.net + record.commission} currencyOriginal={record.currency} />;
            }
        },
        {
            title: t('components.bookings-list.booking-by'),
            dataIndex: "userId",
            sorter: (a: BookingInfo, b: BookingInfo) => a.userName.localeCompare(b.userName),
            key: "userId",
            render: (item: string, record: BookingInfo) => {

                return getUserInfo(record)
            }
        },
        ...(showRewards
            ? [{
                title: t('components.bookings-list.rewards'),
                dataIndex: "rewards",
                key: "rewardsID",
                render: (item: string, record: BookingInfo) => getRewardsInfo(record)
            }]
            : []),
        {
            title: t('components.bookings-list.actions'),
            dataIndex: "actions",
            key: "actions",
            render: (item: string, record: BookingInfo) => {
                return getActions(record);
            }
        }
    ];
    const [loadingBookingDetail, setLoadingBookingDetail] = useState<boolean>(false);


    const getActions = (item: BookingInfo): JSX.Element => {
        return <div className="user-actions">
            <Tooltip title={t('components.bookings-list.actions-tooltip.booking-detail')}>
                <SnippetsOutlined
                    className={`app-icon icon ${loadingBookingDetail ? 'loading-detail' : ''}`}
                    onClick={() => {
                        setLoadingBookingDetail(true);
                        getBookingDetail(item.bookingId, item.locator);
                    }}
                />
            </Tooltip>

            {(item.statusName === 'active' || item.statusName === 'canceled') && (
                <Tooltip title={t('components.bookings-list.actions-tooltip.customer-voucher')}>
                    <UserOutlined className="app-icon icon" onClick={() => { getCustomerVoucher(item.locator) }} />
                </Tooltip>
            )}

        </div>
    }



    const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);

    const handleExpand = (expanded: boolean, record: BookingInfo) => {
        setExpandedRowKeys(expanded ? [record.bookingId] : []);
    };


    return (
        <div className="bookings-list">
            <div className="bookings-resume">
                <Row gutter={16}>
                    <Col xs={24} lg={4}>
                        {t('components.bookings-list.resume.total')}: <span>{props.totalElements}</span>
                    </Col>
                    <Col xs={24} lg={4}>
                        {t('components.bookings-list.resume.total-amount')}: <span>{getTotal('pvp')}</span>
                    </Col>
                    <Col xs={24} lg={4}>
                        {t('components.bookings-list.resume.total-commissions')}: <span>{getTotal('commission')}</span>
                    </Col>
                </Row>
            </div>
            <div className="list">
                {
                    appConfig.device === 'desktop' ?

                        <Table
                            // rowSelection={{}}
                            expandable={{
                                expandedRowKeys,
                                onExpand: handleExpand,
                                expandedRowRender: (record) => (
                                    <p style={{ margin: 0 }}>
                                        <div>
                                            {getprice(record)}
                                        </div>
                                    </p>
                                ),
                            }}
                            locale={{
                                triggerAsc: t('components.bookings-list.sort-asc')!,
                                triggerDesc: t('components.bookings-list.sort-desc')!,
                                cancelSort: t('components.bookings-list.no-sort')!,
                            }}
                            className="app-table"
                            columns={tableColumns}
                            dataSource={bookingsData}
                            loading={props.loading}
                            pagination={false}
                            rowKey={"bookingId"}
                            scroll={{ x: 'max-content' }}
                        />
                        :
                        <List
                            className={`app-list`}
                            itemLayout="vertical"
                            dataSource={bookingsData}
                            loading={props.loading}
                            renderItem={(item: BookingInfo) =>
                                <BookingListItem item={item} getTagStatus={getTagStatus}
                                    getLocatorInfo={getLocatorInfo}
                                    getActions={getActions}
                                    getDatesInfo={getDatesInfo}
                                    getprice={getprice}
                                    getUserInfo={getUserInfo}
                                    getRewardsInfo={getRewardsInfo}
                                    showRewards={showRewards}
                                />
                            }
                        />
                }
            </div>
            {!props.loading && (
                <div className="total-records">
                    <div className="pagination">
                        <Pagination
                            locale={GetPaginationLocale(t)}
                            total={props.bookingsInfo?.totalRecords}
                            pageSizeOptions={["6", "20", "50", "100"]}
                            current={props.page}
                            pageSize={props.pageSize}
                            onChange={(page, pageSize) => {
                                props.search(pageSize, page);
                            }}
                        />

                    </div>
                    <div className="counter">
                        {t('components.company-selector.total-records', [props.bookingsInfo.totalRecords])}
                    </div>
                </div>

            )}

        </div>
    )
}

const SynchronizeButton = (props: { synchronizing: boolean, onSynchronize: (bookingId: string, bookingLocator: string) => void, bookingData: BookingInfo }): JSX.Element => {
    const { t } = useTranslation();


    const waitingTime = 60000;

    const [disabled, setDisabled] = useState<boolean>(false);

    const [counter, setCounter] = useState<number>(waitingTime);

    useEffect(() => {
        let countdown: NodeJS.Timeout | null = null;
        if (disabled) {
            countdown = setTimeout(() => {
                setDisabled(false);
                setCounter(waitingTime);
            }, waitingTime);
        }

        return () => {
            // Limpia el temporizador si el componente se desmonta o si disabled cambia a false
            if (countdown) {
                clearTimeout(countdown);
            }
        };
    }, [disabled]);

    useEffect(() => {
        let interval: NodeJS.Timeout | null = null;

        if (disabled && counter > 0) {
            // Actualiza el contador cada segundo
            interval = setInterval(() => {
                setCounter((prevCounter) => prevCounter - 1000);
            }, 1000);
        }

        return () => {
            // Limpia el intervalo si el componente se desmonta o si disabled cambia a false
            if (interval) {
                clearInterval(interval);
            }
        };
    }, [disabled, counter]);

    return (
        <Popconfirm
            okText={t('components.bookings-list.synchronize.ok-text')}
            cancelText={t('components.bookings-list.synchronize.cancel-text')}
            title={t('components.bookings-list.synchronize.title')}
            description={HTMLReactParser(t('components.bookings-list.synchronize.description', [props.bookingData.locator]))}
            onConfirm={() => {
                if (!disabled) {
                    props.onSynchronize(props.bookingData.bookingId, props.bookingData.locator)
                    setDisabled(true);
                } else {
                    notification.warning({
                        message: t('components.bookings-list.synchronize.time-exceeded'),
                        description: HTMLReactParser(t('components.bookings-list.synchronize.time-exceeded-description', [props.bookingData.locator, (counter / 1000)]))
                    })
                }
            }}
        >
            <Tooltip title={counter == waitingTime ? t('components.bookings-list.actions-tooltip.synchronize-booking') : HTMLReactParser(t('components.bookings-list.synchronize.time-exceeded-description', [props.bookingData.locator, (counter / 1000)]))}>
                <SyncOutlined disabled spin={props.synchronizing} className="app-icon icon" />
            </Tooltip>
        </Popconfirm>
    )
}

export default BookingsListComponent;
